import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, Navigate } from "react-router-dom";
import { api } from "../../../utils/api";
import { LoginHelper } from "../../../utils/LoginHelper";
import { useAdminGate } from "../../../utils/useAdminGate";
import Breadcrumb from "../../common/breadcrumb/Breadcrumb";
import CatalogEmptyState from "../../common/catalog/CatalogEmptyState";
import DetailAccentCard from "../../common/detail/DetailAccentCard";
import EditorFieldSection from "../../common/editor/EditorFieldSection";
import {
	editorFileLabelClassName,
	editorInputClassName,
	editorLabelClassName,
} from "../../common/editor/editorStyles";
import Footer from "../../common/footer/Footer";
import Header from "../../common/header/Header";
import DashboardSidebar from "../DashboardSidebar";
import AdminListCoverThumb from "../AdminListCoverThumb";
import {
	CAROUSEL_TARGET_OPTIONS,
	carouselItemEntityId,
	heroSlideSummary,
	seedEntityOptionsFromItem,
} from "../homepage_carousels/carouselItemTargets";
import ExternalUrlField from "../homepage_carousels/ExternalUrlField";
import AdminEntitySearchSelect from "../homepage_carousels/AdminEntitySearchSelect";
import {
	isValidExternalUrl,
	normalizeExternalUrl,
} from "../homepage_carousels/normalizeExternalUrl";
import { HeroSlideUploadHint } from "./HeroSlideImageGuidelines";
import DashboardImageGuidelinesAside from "../image_guidelines/DashboardImageGuidelinesAside";

const HERO_VIEWPORT_DESKTOP = "desktop";
const HERO_VIEWPORT_MOBILE = "mobile";

const emptyForm = {
	image_file: null,
	target_type: "url",
	url: "",
	order: "0",
	is_visible: true,
	show_webinar_countdown: true,
	viewport: HERO_VIEWPORT_DESKTOP,
	workshop: "",
	webinar: "",
	video_clip: "",
	doctor: "",
};

function slideViewport(slide) {
	return slide?.viewport === HERO_VIEWPORT_MOBILE
		? HERO_VIEWPORT_MOBILE
		: HERO_VIEWPORT_DESKTOP;
}

function seedHeroEntityOptions(slide) {
	if (!slide) return [];
	return seedEntityOptionsFromItem({
		target_type: slide.target_type,
		workshop: slide.workshop,
		webinar: slide.webinar,
		video_clip: slide.video_clip,
		doctor: slide.doctor,
		workshop_title: slide.workshop_title,
		webinar_title: slide.webinar_title,
		video_clip_title: slide.video_clip_title,
		doctor_name: slide.doctor_name,
	});
}

export default function AdminHomepageHero() {
	const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
	const [slides, setSlides] = useState([]);
	const [manageViewport, setManageViewport] = useState(HERO_VIEWPORT_DESKTOP);
	const [form, setForm] = useState(emptyForm);
	const [editingId, setEditingId] = useState(null);
	const [formOpen, setFormOpen] = useState(false);
	const [entityQuery, setEntityQuery] = useState("");
	const [entityOptions, setEntityOptions] = useState([]);
	const [error, setError] = useState("");
	const [notice, setNotice] = useState("");
	const [loading, setLoading] = useState(false);
	const { ready, isAdmin } = useAdminGate();

	const loadSlides = useCallback(() => {
		return api
			.homeHeroAdminList({ token: LoginHelper.getToken() })
			.then((list) => setSlides(Array.isArray(list) ? list : []))
			.catch((err) =>
				setError(err?.data?.message || "دریافت اسلایدها ناموفق بود")
			);
	}, []);

	useEffect(() => {
		const resize = () => setIsDesktop(window.innerWidth > 768);
		window.addEventListener("resize", resize);
		if (isAdmin) loadSlides();
		return () => window.removeEventListener("resize", resize);
	}, [isAdmin, loadSlides]);

	useEffect(() => {
		if (!formOpen || form.target_type === "url") return;
		const timer = window.setTimeout(() => {
			const q = entityQuery.trim();
			const token = LoginHelper.getToken();
			if (form.target_type === "doctor") {
				api
					.doctorSearch({ query: q, page: 1 })
					.then((r) => setEntityOptions(r.doctors || []))
					.catch(() => setEntityOptions([]));
				return;
			}
			if (form.target_type === "workshop") {
				api
					.workshopAdminList({ token, query: q, page: 1 })
					.then((r) => setEntityOptions(r.workshops || []))
					.catch(() => setEntityOptions([]));
				return;
			}
			if (form.target_type === "webinar") {
				api
					.webinarAdminList({ token, query: q, page: 1 })
					.then((r) => setEntityOptions(r.webinars || []))
					.catch(() => setEntityOptions([]));
				return;
			}
			if (form.target_type === "video_clip") {
				api
					.videoClipAdminList({ token, query: q, page: 1 })
					.then((raw) => {
						const clips = Array.isArray(raw) ? raw : raw?.clips || [];
						setEntityOptions(clips);
					})
					.catch(() => setEntityOptions([]));
			}
		}, 300);
		return () => window.clearTimeout(timer);
	}, [entityQuery, form.target_type, formOpen]);

	const visibleSlides = slides.filter(
		(slide) => slideViewport(slide) === manageViewport,
	);

	function openNewSlide() {
		setEditingId(null);
		setForm({ ...emptyForm, viewport: manageViewport });
		setEntityQuery("");
		setEntityOptions([]);
		setFormOpen(true);
		setError("");
		setNotice("");
	}

	function editSlide(slide) {
		setEditingId(slide.id);
		setForm({
			image_file: null,
			target_type: slide.target_type || "url",
			url: slide.url || "",
			order: String(slide.order ?? 0),
			is_visible: slide.is_visible,
			show_webinar_countdown: slide.show_webinar_countdown !== false,
			viewport: slideViewport(slide),
			workshop: slide.workshop ? String(slide.workshop) : "",
			webinar: slide.webinar ? String(slide.webinar) : "",
			video_clip: slide.video_clip ? String(slide.video_clip) : "",
			doctor: slide.doctor ? String(slide.doctor) : "",
		});
		setEntityQuery(heroSlideSummary(slide));
		setEntityOptions(seedHeroEntityOptions(slide));
		setFormOpen(true);
		setError("");
		setNotice("");
	}

	function saveSlide(event) {
		event.preventDefault();
		setLoading(true);
		setError("");
		setNotice("");
		const payload = {
			token: LoginHelper.getToken(),
			...(editingId ? { slide_id: editingId } : {}),
			...(form.image_file ? { image_file: form.image_file } : {}),
			target_type: form.target_type,
			order: Number(form.order),
			is_visible: form.is_visible,
			show_webinar_countdown:
				form.target_type === "webinar" ? form.show_webinar_countdown : false,
			viewport:
				form.viewport === HERO_VIEWPORT_MOBILE
					? HERO_VIEWPORT_MOBILE
					: HERO_VIEWPORT_DESKTOP,
		};
		if (form.target_type === "url") {
			const url = normalizeExternalUrl(form.url);
			if (!isValidExternalUrl(url)) {
				setError("آدرس لینک معتبر نیست (مثال: google.com)");
				setLoading(false);
				return;
			}
			payload.url = url;
		}
		if (form.target_type === "doctor") payload.doctor = form.doctor;
		if (form.target_type === "workshop") payload.workshop = form.workshop;
		if (form.target_type === "webinar") payload.webinar = form.webinar;
		if (form.target_type === "video_clip") payload.video_clip = form.video_clip;

		api
			.homeHeroSave(payload)
			.then(() => {
				setNotice(editingId ? "اسلاید ویرایش شد" : "اسلاید اضافه شد");
				setFormOpen(false);
				setEditingId(null);
				setForm(emptyForm);
				loadSlides();
			})
			.catch((err) =>
				setError(err?.data?.message || "ذخیره اسلاید ناموفق بود")
			)
			.finally(() => setLoading(false));
	}

	function removeSlide(slide) {
		if (!window.confirm("این اسلاید حذف شود؟")) return;
		api
			.homeHeroRemove({
				token: LoginHelper.getToken(),
				slide_id: slide.id,
			})
			.then(loadSlides)
			.catch((err) =>
				setError(err?.data?.message || "حذف اسلاید ناموفق بود")
			);
	}

	if (!ready) return null;
	if (!isAdmin) return <Navigate to="/login/" replace />;

	return (
		<>
			<Helmet>
				<title>مدیریت اسلایدر صفحه اصلی | همراهتیم</title>
			</Helmet>
			<div className="flex min-h-screen flex-col justify-between bg-[#F0F6FC]">
				<div>
					<Header isDesktop={isDesktop} />
					<div className="bg-white">
						<div className="mx-auto max-w-[1200px] px-4">
							<Breadcrumb
								linkDataList={[
									{ label: "خانه", href: "/" },
									{ label: "داشبورد مدیریت", href: "/dashboard/" },
									{
										label: "اسلایدر صفحه اصلی",
										href: "/dashboard/homepage-hero/",
									},
								]}
							/>
						</div>
					</div>
					<main
						className={[
							"mx-auto flex w-full max-w-[1400px] gap-5 py-8",
							isDesktop ? "flex-row px-4" : "flex-col p-[10px]",
						].join(" ")}
					>
						<DashboardSidebar isDesktop={isDesktop} />
						<DashboardImageGuidelinesAside />
						<section className="flex min-w-0 flex-1 flex-col gap-6">
							<DetailAccentCard bodyClassName="p-5 md:p-6">
								<div className="flex flex-wrap items-start justify-between gap-3">
									<div>
										<h1 className="font-Vazir-800 text-[22px] text-darkBlue md:text-[24px]">
											بنر و اسلایدر صفحه اصلی
										</h1>
										<p className="mt-2 max-w-2xl font-Vazir-400 text-[14px] leading-relaxed text-darkBlue/60">
											اسلایدر دسکتاپ و موبایل جداگانه مدیریت می‌شوند؛
											هر کدام تصویر و ترتیب مستقل دارند.
										</p>
										<p className="mt-2 xl:hidden">
											<Link
												to="/dashboard/image-guidelines/"
												className="font-Vazir-600 text-[13px] text-cta hover:underline"
											>
												راهنمای عکس‌ها
											</Link>
										</p>
									</div>
									<button
										type="button"
										onClick={openNewSlide}
										className="rounded-[12px] bg-cta px-5 py-2.5 font-Vazir-700 text-[14px] text-white"
									>
										+ اسلاید جدید
									</button>
								</div>

								<div
									className="mt-5 flex flex-wrap gap-2"
									role="tablist"
									aria-label="نوع اسلایدر"
								>
									<button
										type="button"
										role="tab"
										aria-selected={manageViewport === HERO_VIEWPORT_DESKTOP}
										onClick={() => setManageViewport(HERO_VIEWPORT_DESKTOP)}
										className={[
											"rounded-[10px] px-4 py-2 font-Vazir-600 text-[14px] transition-colors",
											manageViewport === HERO_VIEWPORT_DESKTOP
												? "bg-darkBlue text-white"
												: "bg-lightCta text-darkBlue hover:bg-darkBlue/10",
										].join(" ")}
									>
										اسلایدر دسکتاپ
									</button>
									<button
										type="button"
										role="tab"
										aria-selected={manageViewport === HERO_VIEWPORT_MOBILE}
										onClick={() => setManageViewport(HERO_VIEWPORT_MOBILE)}
										className={[
											"rounded-[10px] px-4 py-2 font-Vazir-600 text-[14px] transition-colors",
											manageViewport === HERO_VIEWPORT_MOBILE
												? "bg-darkBlue text-white"
												: "bg-lightCta text-darkBlue hover:bg-darkBlue/10",
										].join(" ")}
									>
										اسلایدر موبایل
									</button>
								</div>

								{notice && (
									<p className="mt-4 rounded-[10px] bg-green-50 px-4 py-3 text-center font-Vazir-500 text-[14px] text-green-800">
										{notice}
									</p>
								)}
								{error && (
									<p className="mt-4 rounded-[10px] bg-red-50 px-4 py-3 text-center font-Vazir-500 text-[14px] text-[#cc0000]">
										{error}
									</p>
								)}

								<ul className="mt-4 grid gap-3">
									{visibleSlides.length === 0 && (
										<li className="rounded-[12px] border border-dashed border-darkBlue/20 bg-white/80 px-4 py-8 text-center font-Vazir-400 text-[14px] text-darkBlue/55">
											{manageViewport === HERO_VIEWPORT_MOBILE
												? "هنوز اسلایدی برای موبایل ثبت نشده."
												: "هنوز اسلایدی برای دسکتاپ ثبت نشده."}
										</li>
									)}
									{visibleSlides.map((slide, index) => (
										<li
											key={slide.id}
											className="flex flex-wrap items-center gap-4 rounded-[12px] border border-darkBlue/10 bg-white p-4"
										>
											<AdminListCoverThumb
												src={slide.image_url}
												showWhenEmpty
												alt=""
											/>
											<div className="min-w-0 flex-1">
												<h2 className="font-Vazir-700 text-[16px] text-darkBlue">
													اسلاید {index + 1}
													{!slide.is_visible && (
														<span className="mr-2 text-[12px] text-red-600">
															(مخفی)
														</span>
													)}
												</h2>
												<p className="mt-1 font-Vazir-400 text-[13px] text-darkBlue/60">
													{
														CAROUSEL_TARGET_OPTIONS.find(
															(o) => o.value === slide.target_type
														)?.label
													}
													{" · "}
													{heroSlideSummary(slide)}
												</p>
												<p className="font-Vazir-400 text-[12px] text-darkBlue/45">
													{slideViewport(slide) === HERO_VIEWPORT_MOBILE
														? "موبایل"
														: "دسکتاپ"}
													{" · "}
													ترتیب {slide.order}
													{slide.target_type === "webinar" && (
														<>
															{" · "}
															{slide.show_webinar_countdown !== false
																? "شمارش معکوس فعال"
																: "بدون شمارش معکوس"}
														</>
													)}
												</p>
											</div>
											<div className="flex flex-wrap gap-2">
												<button
													type="button"
													onClick={() => editSlide(slide)}
													className="rounded-[10px] bg-lightCta px-4 py-2 font-Vazir-600 text-[13px] text-darkBlue"
												>
													ویرایش
												</button>
												<button
													type="button"
													onClick={() => removeSlide(slide)}
													className="rounded-[10px] border border-red-200 px-4 py-2 font-Vazir-600 text-[13px] text-red-600"
												>
													حذف
												</button>
											</div>
										</li>
									))}
									{slides.length === 0 && (
										<CatalogEmptyState
											message="هنوز اسلایدی برای صفحه اصلی نیست."
											hint="اولین بنر را با تصویر و مقصد بسازید."
											actionLabel="افزودن اسلاید"
											onAction={openNewSlide}
										/>
									)}
								</ul>
							</DetailAccentCard>

							{formOpen && (
								<DetailAccentCard bodyClassName="p-5 md:p-6">
									<h2 className="font-Vazir-800 text-[20px] text-darkBlue">
										{editingId ? "ویرایش اسلاید" : "اسلاید جدید"}
									</h2>
									<form
										onSubmit={saveSlide}
										className="mt-4 flex flex-col gap-4"
									>
										<EditorFieldSection title="تصویر و مقصد">
											<label className={editorFileLabelClassName}>
												<span>
													تصویر بنر
													{editingId && (
														<span className="font-Vazir-400 text-[12px] text-darkBlue/50">
															{" "}
															(برای حفظ تصویر فعلی خالی بگذارید)
														</span>
													)}
												</span>
												<input
													type="file"
													accept="image/*"
													required={!editingId}
													onChange={(e) =>
														setForm({
															...form,
															image_file: e.target.files?.[0] || null,
														})
													}
												/>
												<HeroSlideUploadHint
													viewport={
														form.viewport === HERO_VIEWPORT_MOBILE
															? HERO_VIEWPORT_MOBILE
															: manageViewport
													}
												/>
											</label>
											<label className={editorLabelClassName}>نوع مقصد</label>
											<select
												value={form.target_type}
												onChange={(e) => {
													const nextType = e.target.value;
													setForm({
														...emptyForm,
														target_type: nextType,
														viewport: form.viewport,
														order: form.order,
														is_visible: form.is_visible,
														image_file: form.image_file,
														show_webinar_countdown:
															nextType === "webinar"
																? form.show_webinar_countdown
																: false,
													});
													setEntityQuery("");
													setEntityOptions([]);
												}}
												className={editorInputClassName}
											>
												{CAROUSEL_TARGET_OPTIONS.map((opt) => (
													<option key={opt.value} value={opt.value}>
														{opt.label}
													</option>
												))}
											</select>
											{form.target_type === "url" ? (
												<ExternalUrlField
													value={form.url}
													onChange={(url) => setForm({ ...form, url })}
												/>
											) : (
												<AdminEntitySearchSelect
													required
													value={carouselItemEntityId(form)}
													options={entityOptions}
													searchQuery={entityQuery}
													onSearchQueryChange={setEntityQuery}
													onChange={(id) => {
														const key = form.target_type;
														setForm({
															...form,
															doctor: "",
															workshop: "",
															webinar: "",
															video_clip: "",
															[key]: id,
														});
													}}
												/>
											)}
											<label className="flex items-center gap-2 font-Vazir-400 text-[14px]">
												ترتیب
												<input
													type="number"
													min="0"
													value={form.order}
													onChange={(e) =>
														setForm({ ...form, order: e.target.value })
													}
													className={`${editorInputClassName} max-w-[120px]`}
												/>
											</label>
											<label className="flex items-center gap-2 font-Vazir-400 text-[14px]">
												<input
													type="checkbox"
													checked={form.is_visible}
													onChange={(e) =>
														setForm({
															...form,
															is_visible: e.target.checked,
														})
													}
												/>
												نمایش در صفحه اصلی
											</label>
											{form.target_type === "webinar" && (
												<label className="flex items-center gap-2 font-Vazir-400 text-[14px]">
													<input
														type="checkbox"
														checked={form.show_webinar_countdown}
														onChange={(e) =>
															setForm({
																...form,
																show_webinar_countdown: e.target.checked,
															})
														}
													/>
													نمایش شمارش معکوس / وضعیت وبینار روی بنر
												</label>
											)}
										</EditorFieldSection>
										<div className="flex gap-2">
											<button
												type="submit"
												disabled={loading}
												className="rounded-[12px] bg-cta px-6 py-3 font-Vazir-700 text-white disabled:opacity-60"
											>
												{loading ? "در حال ذخیره…" : "ذخیره"}
											</button>
											<button
												type="button"
												onClick={() => {
													setFormOpen(false);
													setEditingId(null);
												}}
												className="rounded-[12px] border px-6 py-3 font-Vazir-600"
											>
												انصراف
											</button>
										</div>
									</form>
								</DetailAccentCard>
							)}
						</section>
					</main>
				</div>
				<Footer isDesktop={isDesktop} />
			</div>
		</>
	);
}
